import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ListChecks,
  Pencil,
  Play,
  PlayCircle,
  Plus,
  ShieldAlert,
  Trash2,
} from "lucide-react";

import { EvalService } from "@/api";
import type {
  EvalCategory,
  EvalExpect,
  EvalMetric,
  EvalRun,
  EvalScenario,
} from "@/api/eval/eval";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/shared/page-header";
import { useTranslation } from "@/i18n/context";
import {
  type Skill,
  usePlaygroundSkills,
} from "@/playground/use-skills";

/**
 * /evaluation — code-based eval harness page (Sesi 2b).
 *
 * Flow:
 *   1. Operator picks a skill in the workspace.
 *   2. Scenario list for that skill renders + lets the operator
 *      author new scenarios with an "expect" builder.
 *   3. Operator picks a skill version and runs a single scenario
 *      OR a batch over every scenario for that version.
 *   4. Results panel shows PASS/FAIL + k passes + per-attempt
 *      evidence (which checks fired, what the governance trace
 *      showed). Run history lists older runs chronologically.
 *
 * Honest framing: every k attempt is a REAL provider call (BYOK).
 * The header surface this so operators understand the cost shape.
 */
export default function EvaluationPage() {
  const { t } = useTranslation();
  const { skills, loading: skillsLoading, refetch: refetchSkills } =
    usePlaygroundSkills();
  const [activeSkillId, setActiveSkillId] = useState<string | null>(null);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [scenarios, setScenarios] = useState<EvalScenario[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState<string | null>(null);
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editScenario, setEditScenario] = useState<EvalScenario | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const activeSkill = useMemo(
    () => skills.find((s) => s.id === activeSkillId) ?? null,
    [skills, activeSkillId]
  );

  // Deep-link via ``?skill=<id>`` (from the /skills page "Eval status"
  // link). Takes priority over the default-to-first-skill behaviour.
  const [searchParams] = useSearchParams();
  const requestedSkillId = searchParams.get("skill");

  useEffect(() => {
    if (!requestedSkillId || skills.length === 0) return;
    if (!skills.some((s) => s.id === requestedSkillId)) return;
    if (activeSkillId !== requestedSkillId) {
      setActiveSkillId(requestedSkillId);
    }
  }, [requestedSkillId, skills, activeSkillId]);

  // Default to the first skill once the list resolves; mirror its
  // current_version into the active-version picker so the operator
  // can run immediately.
  useEffect(() => {
    if (!activeSkillId && skills.length > 0) {
      setActiveSkillId(skills[0].id);
    }
  }, [skills, activeSkillId]);

  useEffect(() => {
    if (activeSkill && activeSkill.current_version) {
      setActiveVersion(activeSkill.current_version);
    } else {
      setActiveVersion(null);
    }
  }, [activeSkill]);

  const reloadScenarios = useCallback(async () => {
    if (!activeSkillId) {
      setScenarios([]);
      return;
    }
    setScenariosLoading(true);
    setScenariosError(null);
    try {
      const items = await EvalService.listScenarios(activeSkillId);
      setScenarios(items);
    } catch (err) {
      setScenariosError(err instanceof Error ? err.message : String(err));
    } finally {
      setScenariosLoading(false);
    }
  }, [activeSkillId]);

  const reloadRuns = useCallback(async () => {
    if (!activeSkillId) {
      setRuns([]);
      return;
    }
    setRunsLoading(true);
    try {
      const items = await EvalService.listRuns({
        skillId: activeSkillId,
        limit: 100,
      });
      setRuns(items);
    } finally {
      setRunsLoading(false);
    }
  }, [activeSkillId]);

  useEffect(() => {
    void reloadScenarios();
    void reloadRuns();
  }, [reloadScenarios, reloadRuns]);

  const runOne = useCallback(
    async (scenario: EvalScenario) => {
      if (!activeVersion) return;
      const message = t("evaluation.run.confirmSingle", {
        n: scenario.k,
      });
      if (!window.confirm(message)) return;
      setBusyAction(`run-${scenario.id}`);
      try {
        await EvalService.runScenario(scenario.id, activeVersion);
        await reloadRuns();
      } catch (err) {
        window.alert(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyAction(null);
      }
    },
    [activeVersion, reloadRuns, t]
  );

  const runBatch = useCallback(async () => {
    if (!activeSkillId || !activeVersion || scenarios.length === 0) return;
    const total = scenarios.reduce((acc, s) => acc + s.k, 0);
    const message = t("evaluation.run.confirmBatch", {
      n: total,
      scenarios: scenarios.length,
    });
    if (!window.confirm(message)) return;
    setBusyAction("batch");
    try {
      await EvalService.runBatch(activeSkillId, activeVersion);
      await reloadRuns();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyAction(null);
    }
  }, [activeSkillId, activeVersion, scenarios, reloadRuns, t]);

  const deleteScenario = useCallback(
    async (scenario: EvalScenario) => {
      if (!window.confirm(t("evaluation.scenarios.confirmDelete"))) return;
      setBusyAction(`delete-${scenario.id}`);
      try {
        await EvalService.deleteScenario(scenario.id);
        await reloadScenarios();
      } finally {
        setBusyAction(null);
      }
    },
    [reloadScenarios, t]
  );

  // Latest run per scenario for the active version.
  const latestRunByScenario = useMemo(() => {
    const map = new Map<string, EvalRun>();
    for (const run of runs) {
      if (activeVersion !== null && run.skill_version !== activeVersion) continue;
      if (!map.has(run.scenario_id)) {
        map.set(run.scenario_id, run);
      }
    }
    return map;
  }, [runs, activeVersion]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("evaluation.page.title")}
        description={t("evaluation.page.description")}
      />

      <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-[12px] text-warning-900">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5" />
          <span>{t("evaluation.page.costNotice")}</span>
        </div>
      </div>

      <SkillVersionPicker
        skills={skills}
        skillsLoading={skillsLoading}
        activeSkill={activeSkill}
        activeVersion={activeVersion}
        onSelectSkill={setActiveSkillId}
        onSelectVersion={setActiveVersion}
        refetchSkills={refetchSkills}
      />

      {activeSkill && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-[14px] font-medium text-neutral-900">
              {t("evaluation.scenarios.heading", { n: scenarios.length })}
            </h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={runBatch}
                disabled={
                  busyAction === "batch" ||
                  scenarios.length === 0 ||
                  activeVersion === null
                }
              >
                <PlayCircle size={12} />
                {busyAction === "batch"
                  ? t("evaluation.run.batchRunning")
                  : t("evaluation.run.batchButton")}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setCreateOpen(true)}
              >
                <Plus size={12} />
                {t("evaluation.scenarios.newButton")}
              </Button>
            </div>
          </div>

          {scenariosLoading && (
            <p className="text-[12px] text-neutral-500">
              {t("evaluation.scenarios.loading")}
            </p>
          )}
          {scenariosError && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {scenariosError}
            </div>
          )}
          {!scenariosLoading && scenarios.length === 0 && (
            <EmptyScenarios
              onCreate={() => setCreateOpen(true)}
              hasSkill={true}
            />
          )}
          {scenarios.length > 0 && (
            <ul className="space-y-2">
              {scenarios.map((scenario) => (
                <ScenarioRow
                  key={scenario.id}
                  scenario={scenario}
                  latestRun={latestRunByScenario.get(scenario.id) ?? null}
                  onRun={() => runOne(scenario)}
                  onEdit={() => setEditScenario(scenario)}
                  onDelete={() => deleteScenario(scenario)}
                  running={busyAction === `run-${scenario.id}`}
                  deleting={busyAction === `delete-${scenario.id}`}
                  canRun={activeVersion !== null}
                />
              ))}
            </ul>
          )}

          <RunHistory runs={runs} loading={runsLoading} />
        </>
      )}

      {!skillsLoading && skills.length === 0 && (
        <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 px-4 py-5 text-[12px] text-neutral-700">
          {t("evaluation.page.noSkills")}
        </div>
      )}

      {activeSkill && (
        <ScenarioFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          skillId={activeSkill.id}
          onSaved={() => {
            setCreateOpen(false);
            void reloadScenarios();
          }}
        />
      )}
      {editScenario && (
        <ScenarioFormDialog
          open={!!editScenario}
          onClose={() => setEditScenario(null)}
          skillId={editScenario.skill_id}
          initial={editScenario}
          onSaved={() => {
            setEditScenario(null);
            void reloadScenarios();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Skill + version picker
// ============================================================================

function SkillVersionPicker({
  skills,
  skillsLoading,
  activeSkill,
  activeVersion,
  onSelectSkill,
  onSelectVersion,
  refetchSkills,
}: {
  skills: Skill[];
  skillsLoading: boolean;
  activeSkill: Skill | null;
  activeVersion: number | null;
  onSelectSkill(id: string): void;
  onSelectVersion(v: number): void;
  refetchSkills(): void;
}) {
  const { t } = useTranslation();
  if (skillsLoading) {
    return (
      <p className="text-[12px] text-neutral-500">
        {t("evaluation.page.skillsLoading")}
      </p>
    );
  }
  if (skills.length === 0) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <div className="grid gap-3 tablet:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-neutral-700">
            {t("evaluation.picker.skill")}
          </span>
          <select
            value={activeSkill?.id ?? ""}
            onChange={(e) => onSelectSkill(e.target.value)}
            className="block w-full rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          >
            {skills.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · v{s.current_version ?? "?"}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-neutral-700">
            {t("evaluation.picker.version")}
          </span>
          <input
            type="number"
            min={1}
            value={activeVersion ?? ""}
            onChange={(e) => {
              const v = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(v) && v >= 1) onSelectVersion(v);
            }}
            className="mono block w-full rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          />
          <span className="mt-1 block text-[10.5px] text-neutral-500">
            {t("evaluation.picker.versionHint", {
              v: activeSkill?.current_version ?? "?",
            })}{" "}
            <button
              type="button"
              onClick={refetchSkills}
              className="underline hover:text-ocean-600"
            >
              {t("evaluation.picker.refreshSkills")}
            </button>
          </span>
        </label>
      </div>
    </div>
  );
}

// ============================================================================
// Scenario row + result chip
// ============================================================================

function ScenarioRow({
  scenario,
  latestRun,
  onRun,
  onEdit,
  onDelete,
  running,
  deleting,
  canRun,
}: {
  scenario: EvalScenario;
  latestRun: EvalRun | null;
  onRun(): void;
  onEdit(): void;
  onDelete(): void;
  running: boolean;
  deleting: boolean;
  canRun: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const summary = summariseExpect(scenario.expect, t);
  return (
    <li className="rounded-lg border border-neutral-200 bg-white">
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 inline-flex h-5 w-5 items-center justify-center text-neutral-400 hover:text-neutral-700"
          aria-label={expanded ? t("evaluation.scenarios.collapse") : t("evaluation.scenarios.expand")}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <CategoryBadge category={scenario.category} />
            <span className="truncate text-[13px] font-medium text-neutral-900">
              {scenario.name}
            </span>
            <span className="mono rounded bg-neutral-100 px-1.5 py-[1px] text-[10.5px] text-neutral-600">
              {scenario.metric} · k={scenario.k}
            </span>
            <ResultChip run={latestRun} />
          </div>
          <p className="mt-1 text-[12px] text-neutral-600">{summary}</p>
          <p className="mono mt-1 line-clamp-1 text-[10.5px] text-neutral-500">
            {scenario.input_message}
          </p>
        </div>
        <div className="flex shrink-0 items-start gap-1">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil size={11} />
            {t("evaluation.scenarios.editButton")}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onRun}
            disabled={running || !canRun}
          >
            <Play size={11} />
            {running
              ? t("evaluation.run.running")
              : t("evaluation.run.runButton")}
          </Button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            aria-label={t("evaluation.scenarios.deleteButton")}
            title={t("evaluation.scenarios.deleteButton")}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-danger-50 hover:text-danger-600 disabled:opacity-50"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {expanded && latestRun && (
        <div className="border-t border-neutral-100 px-4 py-3">
          <Evidence run={latestRun} />
        </div>
      )}
      {expanded && !latestRun && (
        <div className="border-t border-neutral-100 px-4 py-3 text-[11.5px] text-neutral-500">
          {t("evaluation.scenarios.noRunsYet")}
        </div>
      )}
    </li>
  );
}

function CategoryBadge({ category }: { category: EvalCategory }) {
  const { t } = useTranslation();
  const label =
    category === "pii"
      ? t("evaluation.category.pii")
      : category === "policy"
      ? t("evaluation.category.policy")
      : t("evaluation.category.security");
  const tone =
    category === "pii"
      ? "bg-ocean-50 text-ocean-800"
      : category === "policy"
      ? "bg-warning-50 text-warning-800"
      : "bg-danger-50 text-danger-700";
  return (
    <span
      className={`rounded px-1.5 py-[1px] text-[10.5px] font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

function ResultChip({ run }: { run: EvalRun | null }) {
  const { t } = useTranslation();
  if (!run) {
    return (
      <span className="rounded bg-neutral-100 px-1.5 py-[1px] text-[10.5px] text-neutral-600">
        {t("evaluation.result.never")}
      </span>
    );
  }
  const isPass = run.result === "PASS";
  const tone = isPass
    ? "bg-success-50 text-success-700"
    : "bg-danger-50 text-danger-700";
  return (
    <span
      className={`mono inline-flex items-center gap-1 rounded px-1.5 py-[1px] text-[10.5px] font-medium ${tone}`}
      title={t("evaluation.result.title", {
        result: run.result,
        passes: run.passes,
        k: run.k,
      })}
    >
      {isPass ? <CheckCircle2 size={11} /> : <ShieldAlert size={11} />}
      {run.passes}/{run.k}
    </span>
  );
}

function Evidence({ run }: { run: EvalRun }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-[12px] font-medium text-neutral-800">
        {t("evaluation.evidence.heading", {
          version: run.skill_version,
          when: new Date(run.created_at).toLocaleString(),
        })}
      </p>
      <ul className="mt-2 space-y-2">
        {run.evidence.attempts.map((attempt, i) => (
          <li
            key={i}
            className="rounded border border-neutral-200 bg-neutral-50 p-2"
          >
            <div className="flex items-center gap-2 text-[11.5px]">
              <span
                className={
                  attempt.passed
                    ? "font-mono text-success-700"
                    : "font-mono text-danger-700"
                }
              >
                {attempt.passed
                  ? t("evaluation.evidence.attemptPass", { n: i + 1 })
                  : t("evaluation.evidence.attemptFail", { n: i + 1 })}
              </span>
              <span className="font-mono text-[10.5px] text-neutral-500">
                {attempt.request_id.slice(0, 12)}…
              </span>
            </div>
            <ul className="mt-1 space-y-0.5 text-[11.5px]">
              {Object.entries(attempt.checks).map(([k, v]) => (
                <li key={k} className="font-mono">
                  {v ? "✔" : "✘"} {k}
                </li>
              ))}
            </ul>
            {attempt.terminal_error && (
              <p className="mt-1 font-mono text-[10.5px] text-danger-700">
                {attempt.terminal_error.code}: {attempt.terminal_error.message}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Run history (chronological)
// ============================================================================

function RunHistory({ runs, loading }: { runs: EvalRun[]; loading: boolean }) {
  const { t } = useTranslation();
  return (
    <div>
      <h3 className="text-[13px] font-medium text-neutral-900">
        {t("evaluation.history.heading")}
      </h3>
      {loading && (
        <p className="mt-1 text-[12px] text-neutral-500">
          {t("evaluation.history.loading")}
        </p>
      )}
      {!loading && runs.length === 0 && (
        <p className="mt-1 text-[12px] text-neutral-500">
          {t("evaluation.history.empty")}
        </p>
      )}
      {runs.length > 0 && (
        <ul className="mt-2 space-y-1">
          {runs.slice(0, 20).map((run) => (
            <li
              key={run.id}
              className="flex items-center gap-3 rounded border border-neutral-200 bg-white px-3 py-1.5 text-[11.5px]"
            >
              <span
                className={
                  run.result === "PASS"
                    ? "rounded bg-success-50 px-1.5 py-[1px] font-mono text-success-700"
                    : "rounded bg-danger-50 px-1.5 py-[1px] font-mono text-danger-700"
                }
              >
                {run.result}
              </span>
              <span className="mono">
                v{run.skill_version} · {run.metric} · {run.passes}/{run.k}
              </span>
              <span className="text-neutral-500">
                {new Date(run.created_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Scenario create / edit dialog
// ============================================================================

interface FormState {
  name: string;
  category: EvalCategory;
  input_message: string;
  pii_flagged: "any" | "yes" | "no";
  pii_types: string;
  policy_blocked: boolean;
  security_flagged: boolean;
  no_content: boolean;
  metric: EvalMetric;
  k: number;
}

const EMPTY_FORM: FormState = {
  name: "",
  category: "pii",
  input_message: "",
  pii_flagged: "any",
  pii_types: "",
  policy_blocked: false,
  security_flagged: false,
  no_content: false,
  metric: "pass^k",
  k: 3,
};

function fromScenario(s: EvalScenario): FormState {
  const e: EvalExpect = s.expect;
  return {
    name: s.name,
    category: s.category,
    input_message: s.input_message,
    pii_flagged:
      "pii_flagged" in e
        ? e.pii_flagged
          ? "yes"
          : "no"
        : "any",
    pii_types: (e.pii_types_include ?? []).join(", "),
    policy_blocked: e.policy_blocked === true,
    security_flagged: e.security_flagged === true,
    no_content: e.no_content === true,
    metric: s.metric,
    k: s.k,
  };
}

function buildExpect(state: FormState): EvalExpect {
  const expect: EvalExpect = {};
  if (state.pii_flagged !== "any") {
    expect.pii_flagged = state.pii_flagged === "yes";
  }
  const types = state.pii_types
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  if (types.length > 0) {
    expect.pii_types_include = types;
  }
  if (state.policy_blocked) expect.policy_blocked = true;
  if (state.security_flagged) expect.security_flagged = true;
  if (state.no_content) expect.no_content = true;
  return expect;
}

function ScenarioFormDialog({
  open,
  onClose,
  skillId,
  initial,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  skillId: string;
  initial?: EvalScenario;
  onSaved(): void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<FormState>(
    initial ? fromScenario(initial) : EMPTY_FORM
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setState(initial ? fromScenario(initial) : EMPTY_FORM);
      setError(null);
    }
  }, [open, initial]);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const expect = buildExpect(state);
      if (Object.keys(expect).length === 0) {
        setError(t("evaluation.form.errorEmptyExpect"));
        setBusy(false);
        return;
      }
      if (initial) {
        await EvalService.updateScenario(initial.id, {
          name: state.name,
          input_message: state.input_message,
          expect,
          metric: state.metric,
          k: state.k,
        });
      } else {
        await EvalService.createScenario({
          skill_id: skillId,
          name: state.name,
          category: state.category,
          input_message: state.input_message,
          expect,
          metric: state.metric,
          k: state.k,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [state, initial, skillId, onSaved, t]);

  return (
    <Dialog open={open} onClose={onClose} size="max-w-xl">
      <DialogHeader>
        <DialogTitle>
          {initial
            ? t("evaluation.form.editTitle")
            : t("evaluation.form.createTitle")}
        </DialogTitle>
        <DialogDescription>
          {t("evaluation.form.description")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <Field label={t("evaluation.form.name")}>
          <input
            type="text"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            maxLength={120}
            className="block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
          />
        </Field>
        {!initial && (
          <Field label={t("evaluation.form.category")}>
            <select
              value={state.category}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  category: e.target.value as EvalCategory,
                }))
              }
              className="block w-full rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            >
              <option value="pii">{t("evaluation.category.pii")}</option>
              <option value="policy">{t("evaluation.category.policy")}</option>
              <option value="security">
                {t("evaluation.category.security")}
              </option>
            </select>
          </Field>
        )}
        <Field label={t("evaluation.form.inputMessage")}>
          <textarea
            value={state.input_message}
            onChange={(e) =>
              setState((s) => ({ ...s, input_message: e.target.value }))
            }
            rows={4}
            className="mono block w-full resize-y rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] outline-none focus:border-ocean-400"
          />
        </Field>

        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
          <p className="mb-2 text-[12px] font-medium text-neutral-800">
            {t("evaluation.form.expectHeading")}
          </p>

          <Field label={t("evaluation.form.expectPiiFlagged")} inline>
            <select
              value={state.pii_flagged}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  pii_flagged: e.target.value as FormState["pii_flagged"],
                }))
              }
              className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-[12px]"
            >
              <option value="any">{t("evaluation.form.expectAny")}</option>
              <option value="yes">{t("evaluation.form.expectYes")}</option>
              <option value="no">{t("evaluation.form.expectNo")}</option>
            </select>
          </Field>

          <Field label={t("evaluation.form.expectPiiTypes")}>
            <input
              type="text"
              value={state.pii_types}
              onChange={(e) =>
                setState((s) => ({ ...s, pii_types: e.target.value }))
              }
              placeholder="NIK, EMAIL"
              className="mono block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[12px] outline-none focus:border-ocean-400"
            />
            <span className="mt-1 block text-[10.5px] text-neutral-500">
              {t("evaluation.form.expectPiiTypesHint")}
            </span>
          </Field>

          <Toggle
            checked={state.policy_blocked}
            onChange={(v) => setState((s) => ({ ...s, policy_blocked: v }))}
            label={t("evaluation.form.expectPolicyBlocked")}
          />
          <Toggle
            checked={state.security_flagged}
            onChange={(v) => setState((s) => ({ ...s, security_flagged: v }))}
            label={t("evaluation.form.expectSecurityFlagged")}
          />
          <Toggle
            checked={state.no_content}
            onChange={(v) => setState((s) => ({ ...s, no_content: v }))}
            label={t("evaluation.form.expectNoContent")}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("evaluation.form.metric")}>
            <select
              value={state.metric}
              onChange={(e) =>
                setState((s) => ({ ...s, metric: e.target.value as EvalMetric }))
              }
              className="block w-full rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            >
              <option value="pass^k">{t("evaluation.form.metricCaret")}</option>
              <option value="pass@k">{t("evaluation.form.metricAt")}</option>
            </select>
          </Field>
          <Field label={t("evaluation.form.k")}>
            <input
              type="number"
              min={1}
              max={10}
              value={state.k}
              onChange={(e) => {
                const v = Number.parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) setState((s) => ({ ...s, k: v }));
              }}
              className="mono block w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-[13px] outline-none focus:border-ocean-400"
            />
            <span className="mt-1 block text-[10.5px] text-neutral-500">
              {t("evaluation.form.kHint")}
            </span>
          </Field>
        </div>
        {error && (
          <div className="rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-[11.5px] text-danger-700">
            {error}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          {t("evaluation.form.cancel")}
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={submit}
          disabled={busy || !state.name.trim() || !state.input_message.trim()}
        >
          {busy ? t("evaluation.form.saving") : t("evaluation.form.save")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ============================================================================
// Tiny helpers
// ============================================================================

function Field({
  label,
  inline,
  children,
}: {
  label: string;
  inline?: boolean;
  children: React.ReactNode;
}) {
  if (inline) {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-[12px] text-neutral-700">{label}</span>
        {children}
      </div>
    );
  }
  return (
    <label className="mt-2 block">
      <span className="mb-1 block text-[12px] font-medium text-neutral-800">
        {label}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange(v: boolean): void;
  label: string;
}) {
  return (
    <label className="mt-1.5 flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-[12px] text-neutral-700">{label}</span>
    </label>
  );
}

function EmptyScenarios({
  onCreate,
  hasSkill,
}: {
  onCreate(): void;
  hasSkill: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 p-5">
      <div className="flex items-center gap-2 text-ocean-900">
        <ListChecks size={14} />
        <span className="text-[13px] font-medium">
          {t("evaluation.scenarios.emptyTitle")}
        </span>
      </div>
      <p className="mt-1 text-[12px] text-neutral-700">
        {t("evaluation.scenarios.emptyBody")}
      </p>
      {hasSkill && (
        <Button variant="default" size="sm" onClick={onCreate} className="mt-3">
          <Plus size={12} />
          {t("evaluation.scenarios.newButton")}
        </Button>
      )}
    </div>
  );
}

function summariseExpect(
  expect: EvalExpect,
  t: ReturnType<typeof useTranslation>["t"]
): string {
  const parts: string[] = [];
  if (expect.pii_flagged === true) parts.push(t("evaluation.summary.piiFlagged"));
  if (expect.pii_flagged === false) parts.push(t("evaluation.summary.piiClean"));
  if (expect.pii_types_include && expect.pii_types_include.length > 0) {
    parts.push(
      t("evaluation.summary.piiTypes", {
        types: expect.pii_types_include.join(", "),
      })
    );
  }
  if (expect.policy_blocked) parts.push(t("evaluation.summary.policyBlocked"));
  if (expect.security_flagged) parts.push(t("evaluation.summary.securityFlagged"));
  if (expect.no_content) parts.push(t("evaluation.summary.noContent"));
  return parts.join(" · ") || t("evaluation.summary.empty");
}
